export type MediaKind = 'image' | 'video'

const IMAGE_EXT_TO_MIME: Record<string, string> = {
  avif: 'image/avif',
  bmp: 'image/bmp',
  gif: 'image/gif',
  heic: 'image/heic',
  heif: 'image/heif',
  ico: 'image/x-icon',
  jfif: 'image/jpeg',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  png: 'image/png',
  svg: 'image/svg+xml',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  webp: 'image/webp',
}

const VIDEO_EXT_TO_MIME: Record<string, string> = {
  '3g2': 'video/3gpp2',
  '3gp': 'video/3gpp',
  avi: 'video/x-msvideo',
  flv: 'video/x-flv',
  m4v: 'video/x-m4v',
  mkv: 'video/x-matroska',
  mov: 'video/quicktime',
  mp4: 'video/mp4',
  mpeg: 'video/mpeg',
  mpg: 'video/mpeg',
  m3u8: 'application/vnd.apple.mpegurl',
  mpd: 'application/dash+xml',
  ogv: 'video/ogg',
  ogg: 'video/ogg',
  ts: 'video/mp2t',
  webm: 'video/webm',
  wmv: 'video/x-ms-wmv',
}

const STREAMING_VIDEO_MIMES = new Set([
  'application/dash+xml',
  'application/vnd.apple.mpegurl',
  'application/x-mpegurl',
])

export function getFileExtension(fileName: string): string {
  const base = fileName.split(/[/\\]/).pop() || fileName
  const dot = base.lastIndexOf('.')
  if (dot <= 0) return ''
  return base.slice(dot + 1).toLowerCase()
}

function isImageMime(mime: string): boolean {
  return mime.startsWith('image/')
}

function isVideoMime(mime: string): boolean {
  const lower = mime.toLowerCase()
  return lower.startsWith('video/') || STREAMING_VIDEO_MIMES.has(lower)
}

/** Detect whether a file is an image or video from MIME type and/or extension. */
export function detectMediaKind(file: File): MediaKind | null {
  const mime = (file.type || '').trim().toLowerCase()
  if (mime && mime !== 'application/octet-stream') {
    if (isImageMime(mime)) return 'image'
    if (isVideoMime(mime)) return 'video'
    return null
  }

  const ext = getFileExtension(file.name)
  if (IMAGE_EXT_TO_MIME[ext]) return 'image'
  if (VIDEO_EXT_TO_MIME[ext]) return 'video'
  return null
}

/** Returns an error message when the file does not match the expected upload slot. */
export function validateMediaFileForKind(file: File, kind: MediaKind): string | null {
  const detected = detectMediaKind(file)
  if (detected === kind) return null
  if (detected === 'video' && kind === 'image') {
    return 'Product image must be an image file. Videos are not allowed here.'
  }
  if (detected === 'image' && kind === 'video') {
    return 'Product video must be a video file. Images are not allowed here.'
  }
  return kind === 'image'
    ? 'Unsupported image format. Use PNG, JPEG, WebP, GIF, HEIC, SVG, etc.'
    : 'Unsupported video format. Use MP4, MOV, WebM, MKV, etc.'
}

/** Resolve a Content-Type for GCS signed uploads from browser File metadata. */
export function resolveMediaContentType(file: File, kind: MediaKind): string {
  const detected = detectMediaKind(file)
  if (detected !== kind) {
    return kind === 'image' ? 'image/jpeg' : 'video/mp4'
  }

  const mime = (file.type || '').trim().toLowerCase()
  const ext = getFileExtension(file.name)
  const byExt = (kind === 'image' ? IMAGE_EXT_TO_MIME : VIDEO_EXT_TO_MIME)[ext]

  if (mime && mime !== 'application/octet-stream') {
    if (kind === 'image' && isImageMime(mime)) return mime
    if (kind === 'video' && isVideoMime(mime)) return mime
  }

  if (byExt) return byExt

  return kind === 'image' ? 'image/jpeg' : 'video/mp4'
}

export function defaultExtensionForKind(kind: MediaKind): string {
  return kind === 'image' ? 'jpg' : 'mp4'
}

export function extensionForContentType(contentType: string, kind: MediaKind): string {
  const lower = contentType.toLowerCase()
  const map = kind === 'image' ? IMAGE_EXT_TO_MIME : VIDEO_EXT_TO_MIME
  const match = Object.entries(map).find(([, mime]) => mime === lower)
  return match?.[0] || defaultExtensionForKind(kind)
}

/** Browser file-picker accept strings — image/* and video/* plus common extensions. */
export const IMAGE_UPLOAD_ACCEPT =
  'image/*,.avif,.bmp,.gif,.heic,.heif,.ico,.jfif,.jpeg,.jpg,.png,.svg,.tif,.tiff,.webp'
export const VIDEO_UPLOAD_ACCEPT =
  'video/*,.3gp,.3g2,.avi,.flv,.m4v,.mkv,.mov,.mp4,.mpeg,.mpg,.m3u8,.mpd,.ogv,.ogg,.ts,.webm,.wmv'
